/**
 * ISO-3166 alpha-2 country code → centroid lat/lon + display name.
 *
 * Used by `/r/<code>` to pre-pan the threat-radar globe to a specific
 * region without a geo-lookup. Coverage is hand-curated for the
 * countries we actually surface in press decks + sales: G20 + EU27 +
 * notable cyber/Web3 jurisdictions. Fallback when an unknown code is
 * passed is the world centre.
 *
 * Centroids are visual centres (eyeballed for the radar's sense of
 * "here's the country") rather than population-weighted, because the
 * goal is camera framing, not statistics.
 */
const COUNTRY_CENTROIDS = {
  AE: { lat: 24.0,    lon:  54.0,   name: "United Arab Emirates" },
  AR: { lat: -34.0,   lon: -64.0,   name: "Argentina"            },
  AT: { lat: 47.7,    lon:  13.3,   name: "Austria"              },
  AU: { lat: -25.3,   lon: 133.8,   name: "Australia"            },
  BE: { lat: 50.85,   lon:   4.35,  name: "Belgium"              },
  BG: { lat: 42.7,    lon:  25.5,   name: "Bulgaria"             },
  BR: { lat: -14.2,   lon: -51.9,   name: "Brazil"               },
  CA: { lat: 56.1,    lon: -106.3,  name: "Canada"               },
  CH: { lat: 46.8,    lon:   8.2,   name: "Switzerland"          },
  CL: { lat: -35.7,   lon: -71.5,   name: "Chile"                },
  CN: { lat: 35.9,    lon: 104.2,   name: "China"                },
  CO: { lat:   4.6,   lon: -74.3,   name: "Colombia"             },
  CY: { lat: 35.1,    lon:  33.4,   name: "Cyprus"               },
  CZ: { lat: 49.8,    lon:  15.5,   name: "Czechia"              },
  DE: { lat: 51.2,    lon:  10.45,  name: "Germany"              },
  DK: { lat: 56.3,    lon:   9.5,   name: "Denmark"              },
  EE: { lat: 58.6,    lon:  25.0,   name: "Estonia"              },
  EG: { lat: 26.8,    lon:  30.8,   name: "Egypt"                },
  ES: { lat: 40.5,    lon:  -3.7,   name: "Spain"                },
  FI: { lat: 64.0,    lon:  26.0,   name: "Finland"              },
  FR: { lat: 46.6,    lon:   2.2,   name: "France"               },
  GB: { lat: 54.0,    lon:  -2.0,   name: "United Kingdom"       },
  GH: { lat:  7.95,   lon:  -1.0,   name: "Ghana"                },
  GR: { lat: 39.0,    lon:  22.0,   name: "Greece"               },
  HK: { lat: 22.3,    lon: 114.2,   name: "Hong Kong"            },
  HR: { lat: 45.1,    lon:  15.2,   name: "Croatia"              },
  HU: { lat: 47.2,    lon:  19.5,   name: "Hungary"              },
  ID: { lat:  -2.5,   lon: 118.0,   name: "Indonesia"            },
  IE: { lat: 53.4,    lon:  -8.2,   name: "Ireland"              },
  IL: { lat: 31.0,    lon:  35.0,   name: "Israel"               },
  IN: { lat: 22.0,    lon:  78.5,   name: "India"                },
  IR: { lat: 32.4,    lon:  53.7,   name: "Iran"                 },
  IS: { lat: 64.96,   lon: -19.02,  name: "Iceland"              },
  IT: { lat: 42.5,    lon:  12.6,   name: "Italy"                },
  JP: { lat: 36.2,    lon: 138.3,   name: "Japan"                },
  KE: { lat:  -0.02,  lon:  37.9,   name: "Kenya"                },
  KP: { lat: 40.34,   lon: 127.51,  name: "North Korea"          },
  KR: { lat: 35.9,    lon: 127.8,   name: "South Korea"          },
  KW: { lat: 29.3,    lon:  47.5,   name: "Kuwait"               },
  LU: { lat: 49.8,    lon:   6.1,   name: "Luxembourg"           },
  LV: { lat: 56.9,    lon:  24.6,   name: "Latvia"               },
  MA: { lat: 31.8,    lon:  -7.1,   name: "Morocco"              },
  MT: { lat: 35.94,   lon:  14.38,  name: "Malta"                },
  MX: { lat: 23.6,    lon: -102.5,  name: "Mexico"               },
  MY: { lat:  4.2,    lon: 101.9,   name: "Malaysia"             },
  NG: { lat:  9.1,    lon:   8.7,   name: "Nigeria"              },
  NL: { lat: 52.1,    lon:   5.3,   name: "Netherlands"          },
  NO: { lat: 60.5,    lon:   8.5,   name: "Norway"               },
  NZ: { lat: -41.0,   lon: 174.0,   name: "New Zealand"          },
  PE: { lat:  -9.2,   lon: -75.0,   name: "Peru"                 },
  PH: { lat: 12.9,    lon: 121.8,   name: "Philippines"          },
  PK: { lat: 30.4,    lon:  69.3,   name: "Pakistan"             },
  PL: { lat: 51.9,    lon:  19.1,   name: "Poland"               },
  PT: { lat: 39.4,    lon:  -8.2,   name: "Portugal"             },
  QA: { lat: 25.4,    lon:  51.2,   name: "Qatar"                },
  RO: { lat: 45.9,    lon:  24.97,  name: "Romania"              },
  RS: { lat: 44.0,    lon:  21.0,   name: "Serbia"               },
  RU: { lat: 61.5,    lon:  90.0,   name: "Russia"               },
  SA: { lat: 23.9,    lon:  45.1,   name: "Saudi Arabia"         },
  SE: { lat: 60.1,    lon:  18.6,   name: "Sweden"               },
  SG: { lat:  1.35,   lon: 103.82,  name: "Singapore"            },
  SI: { lat: 46.15,   lon:  14.99,  name: "Slovenia"             },
  SK: { lat: 48.7,    lon:  19.7,   name: "Slovakia"             },
  TH: { lat: 15.9,    lon: 100.99,  name: "Thailand"             },
  TR: { lat: 39.0,    lon:  35.2,   name: "Türkiye"              },
  TW: { lat: 23.7,    lon: 121.0,   name: "Taiwan"               },
  UA: { lat: 48.4,    lon:  31.2,   name: "Ukraine"              },
  US: { lat: 39.8,    lon: -95.6,   name: "United States"        },
  VN: { lat: 14.06,   lon: 108.28,  name: "Vietnam"              },
  ZA: { lat: -30.6,   lon:  22.95,  name: "South Africa"         },
};

export default COUNTRY_CENTROIDS;
